import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, ArrowLeft, Check, Ruler, Factory, Settings2, User, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formError = (err, t) => t(err.response?.status === 429 ? 'form.rateLimited' : err.response?.status === 422 ? 'form.invalid' : 'form.error');

const steps = [
  { icon: Ruler, key: 'step1' },
  { icon: Factory, key: 'step2' },
  { icon: Settings2, key: 'step3' },
  { icon: User, key: 'step4' },
];

export default function HallPlannerPage() {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [config, setConfig] = useState({ usages: [], features: [], dimensions: {} });
  const [form, setForm] = useState({
    length: '', width: '', height: '', usage: '', features: [], name: '', email: '', phone: '', company: '', notes: '',
  });

  useEffect(() => {
    axios.get(`${API}/planner/config`).then(r => {
      const cfg = r.data || { usages: [], features: [], dimensions: {} };
      setConfig(cfg);
      // Pre-check default features
      const defaults = (cfg.features || []).filter(f => f.default_checked).map(f => f.id);
      if (defaults.length) setForm(prev => ({ ...prev, features: defaults }));
    }).catch(() => {});
  }, []);

  const labelOf = (item) => item[`label_${language}`] || item.label_de || '';
  const descOf = (item) => item[`description_${language}`] || item.description_de || '';

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const toggleFeature = (id) => {
    setForm(prev => ({
      ...prev,
      features: prev.features.includes(id) ? prev.features.filter(f => f !== id) : [...prev.features, id]
    }));
  };

  const dims = config.dimensions || {};
  const numOrUndef = (v) => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };
  const minL = numOrUndef(dims.planner_min_length), maxL = numOrUndef(dims.planner_max_length);
  const minW = numOrUndef(dims.planner_min_width), maxW = numOrUndef(dims.planner_max_width);
  const minH = numOrUndef(dims.planner_min_height), maxH = numOrUndef(dims.planner_max_height);

  const area = (parseFloat(form.length) || 0) * (parseFloat(form.width) || 0);

  const inRange = (val, min, max) => {
    const n = parseFloat(val);
    if (isNaN(n) || n <= 0) return false;
    if (min !== undefined && n < min) return false;
    if (max !== undefined && n > max) return false;
    return true;
  };

  const canNext = () => {
    if (step === 0) return inRange(form.length, minL, maxL) && inRange(form.width, minW, maxW) && inRange(form.height, minH, maxH);
    if (step === 1) return form.usage;
    if (step === 3) return form.name && form.email;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/hall-inquiries`, {
        ...form,
        length: parseFloat(form.length) || 0,
        width: parseFloat(form.width) || 0,
        height: parseFloat(form.height) || 0,
      });
      setSubmittedId(res.data.id);
      setSubmitted(true);
      toast.success(t('planner.success'));
    } catch (err) {
      toast.error(formError(err, t));
    }
    setLoading(false);
  };

  const downloadPDF = () => {
    if (submittedId) {
      window.open(`${API}/hall-inquiries/${submittedId}/pdf?lang=${language}`, '_blank');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="planner-success">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">{t('planner.success')}</h2>
          <p className="text-slate-600 mb-2 font-mono text-sm">{form.length}m x {form.width}m x {form.height}m = {area.toLocaleString()} m²</p>
          {submittedId && (
            <Button onClick={downloadPDF} className="mt-6 bg-[#066ce7] hover:bg-[#005bb5] text-white px-8 py-3 font-semibold" data-testid="download-pdf-btn">
              <FileDown className="w-5 h-5 mr-2" /> {language === 'de' ? 'Spezifikation als PDF herunterladen' : language === 'en' ? 'Download Specification as PDF' : 'Descargar Especificación en PDF'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="hall-planner-page">
      <SEO page="planner" />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-[#3b8dff] text-sm font-semibold uppercase tracking-wider mb-4">{t('planner.tag')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6" data-testid="planner-title">{t('planner.title')}</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">{t('planner.subtitle')}</p>
        </div>
      </section>

      {/* Planner */}
      <section className="py-20 md:py-32 blueprint-bg">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {/* Steps indicator */}
          <div className="flex items-center justify-center mb-12" data-testid="planner-steps">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center">
                <button
                  onClick={() => i < step && setStep(i)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    i <= step ? 'bg-[#066ce7] text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-200'
                  }`}
                  data-testid={`step-indicator-${i}`}
                >
                  {i < step ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                </button>
                {i < steps.length - 1 && (
                  <div className={`w-16 md:w-24 h-0.5 mx-2 ${i < step ? 'bg-[#066ce7]' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 md:p-10">
            <h3 className="text-xl font-bold text-[#0F172A] mb-6">{t(`planner.${steps[step].key}`)}</h3>

            {/* Step 0: Dimensions */}
            {step === 0 && (
              <div className="space-y-6" data-testid="planner-step-dimensions">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('planner.length')}{minL || maxL ? <span className="text-xs text-slate-400 ml-1">({minL ?? '–'}–{maxL ?? '–'} m)</span> : null}</Label>
                    <Input type="number" min={minL} max={maxL} value={form.length} onChange={e => updateForm('length', e.target.value)} placeholder="z.B. 30" className="font-mono" data-testid="input-length" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('planner.width')}{minW || maxW ? <span className="text-xs text-slate-400 ml-1">({minW ?? '–'}–{maxW ?? '–'} m)</span> : null}</Label>
                    <Input type="number" min={minW} max={maxW} value={form.width} onChange={e => updateForm('width', e.target.value)} placeholder="z.B. 20" className="font-mono" data-testid="input-width" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('planner.height')}{minH || maxH ? <span className="text-xs text-slate-400 ml-1">({minH ?? '–'}–{maxH ?? '–'} m)</span> : null}</Label>
                    <Input type="number" min={minH} max={maxH} value={form.height} onChange={e => updateForm('height', e.target.value)} placeholder="z.B. 8" className="font-mono" data-testid="input-height" />
                  </div>
                </div>
                {area > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between" data-testid="area-display">
                    <span className="text-sm font-semibold text-slate-700">{t('planner.area')}</span>
                    <span className="font-mono text-2xl font-bold text-[#066ce7]">{area.toLocaleString()} m2</span>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Usage */}
            {step === 1 && (
              <div data-testid="planner-step-usage">
                <Label className="text-sm font-semibold text-slate-700 mb-3 block">{t('planner.usage')}</Label>
                <Select value={form.usage} onValueChange={v => updateForm('usage', v)}>
                  <SelectTrigger data-testid="select-usage">
                    <SelectValue placeholder={t('planner.usage')} />
                  </SelectTrigger>
                  <SelectContent>
                    {config.usages.map(u => (
                      <SelectItem key={u.id} value={u.id} data-testid={`usage-option-${u.id}`}>{labelOf(u)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 2: Features */}
            {step === 2 && (
              <div className="space-y-4" data-testid="planner-step-features">
                <Label className="text-sm font-semibold text-slate-700 block">{t('planner.features')}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {config.features.map(feat => (
                    <label key={feat.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#066ce7] cursor-pointer transition-colors" data-testid={`feature-${feat.id}`} title={descOf(feat)}>
                      <Checkbox checked={form.features.includes(feat.id)} onCheckedChange={() => toggleFeature(feat.id)} className="mt-0.5" />
                      <div>
                        <span className="text-sm text-slate-700 block">{labelOf(feat)}</span>
                        {descOf(feat) && <span className="text-xs text-slate-500 block mt-0.5">{descOf(feat)}</span>}
                      </div>
                    </label>
                  ))}
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('planner.notes')}</Label>
                  <Textarea value={form.notes} onChange={e => updateForm('notes', e.target.value)} rows={3} data-testid="input-notes" />
                </div>
              </div>
            )}

            {/* Step 3: Contact */}
            {step === 3 && (
              <div className="space-y-4" data-testid="planner-step-contact">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.name')} *</Label>
                    <Input value={form.name} onChange={e => updateForm('name', e.target.value)} data-testid="input-planner-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.email')} *</Label>
                    <Input type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} data-testid="input-planner-email" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.phone')}</Label>
                    <Input value={form.phone} onChange={e => updateForm('phone', e.target.value)} data-testid="input-planner-phone" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.company')}</Label>
                    <Input value={form.company} onChange={e => updateForm('company', e.target.value)} data-testid="input-planner-company" />
                  </div>
                </div>
                {/* Summary */}
                <div className="bg-slate-50 rounded-lg p-5 mt-4 space-y-2" data-testid="planner-summary">
                  <h4 className="font-bold text-sm text-[#0F172A] mb-3">{t('planner.area')}: <span className="font-mono text-[#066ce7]">{area.toLocaleString()} m2</span></h4>
                  <p className="text-sm text-slate-600">{form.length}m x {form.width}m x {form.height}m</p>
                  <p className="text-sm text-slate-600">{t('planner.usage')}: {labelOf(config.usages.find(u => u.id === form.usage) || {})}</p>
                  {form.features.length > 0 && (
                    <p className="text-sm text-slate-600">{t('planner.features')}: {form.features.map(id => labelOf(config.features.find(f => f.id === id) || {})).filter(Boolean).join(', ')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} data-testid="planner-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('planner.back')}
              </Button>
              {step < 3 ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="planner-next-btn">
                  {t('planner.next')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={!canNext() || loading} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="planner-submit-btn">
                  {loading ? '...' : t('planner.submit')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
